import React, { createContext, useContext, useState, useCallback } from 'react';

interface DemoContextType {
  isDemo: boolean;
  toggleDemo: () => void;
  roleOverride: string | null;
  setRoleOverride: (role: string | null) => void;
  viewAsEmployee: string | null;
  setViewAsEmployee: (employeeId: string | null) => void;
  companyOverride: number | null;
  setCompanyOverride: (companyId: number | null) => void;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export const DEMO_COMPANIES = [
  { id: 1, code: 'TSC', name: '티사이언티픽' },
  { id: 2, code: 'TSD', name: 'TS Digital' },
  { id: 3, code: 'TSB', name: 'TS Bio' },
] as const;

export const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDemo, setIsDemo] = useState<boolean>(() => localStorage.getItem('demo_mode') === 'true');
  const [roleOverride, setRoleOverrideState] = useState<string | null>(
    () => localStorage.getItem('role_override')
  );
  const [viewAsEmployee, setViewAsEmployeeState] = useState<string | null>(
    () => localStorage.getItem('view_as_employee')
  );
  const [companyOverride, setCompanyOverrideState] = useState<number | null>(
    () => {
      const v = localStorage.getItem('company_override');
      return v ? parseInt(v, 10) : null;
    }
  );

  const toggleDemo = useCallback(() => {
    setIsDemo(prev => {
      const next = !prev;
      if (next) {
        localStorage.setItem('demo_mode', 'true');
      } else {
        localStorage.removeItem('demo_mode');
        localStorage.removeItem('company_override');
        setCompanyOverrideState(null);
      }
      return next;
    });
  }, []);

  const setRoleOverride = useCallback((role: string | null) => {
    setRoleOverrideState(role);
    if (role) {
      localStorage.setItem('role_override', role);
    } else {
      localStorage.removeItem('role_override');
    }
  }, []);

  const setViewAsEmployee = useCallback((employeeId: string | null) => {
    setViewAsEmployeeState(employeeId);
    if (employeeId) {
      localStorage.setItem('view_as_employee', employeeId);
    } else {
      localStorage.removeItem('view_as_employee');
    }
  }, []);

  const setCompanyOverride = useCallback((companyId: number | null) => {
    setCompanyOverrideState(companyId);
    if (companyId) {
      localStorage.setItem('company_override', String(companyId));
    } else {
      localStorage.removeItem('company_override');
    }
  }, []);

  return (
    <DemoContext.Provider value={{ isDemo, toggleDemo, roleOverride, setRoleOverride, viewAsEmployee, setViewAsEmployee, companyOverride, setCompanyOverride }}>
      {children}
    </DemoContext.Provider>
  );
};

export const useDemo = (): DemoContextType => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};
